import React, { useState, useEffect } from 'react'
import { LayoutGrid, Compass, Clock, Star, Settings, User, Menu, X, Crown, PanelLeftClose, PanelLeft, ChevronLeft, ChevronRight, Search, Terminal } from 'lucide-react'
import Logo from './Logo'
import { useAuth } from '../../context/AuthContext'
import { games as modDatabase } from '../../data/gameData'

const Sidebar = ({ currentPage, onNavigate, searchTerm, setSearchTerm, runningGames }) => {
    const [isOpen, setIsOpen] = useState(false)
    const [isHovered, setIsHovered] = useState(false)
    const { user } = useAuth()
    
    // Auto-collapse if not hovered, but force expand if mobile menu is open
    const isCollapsed = !isHovered && !isOpen;

    const closeMenu = () => setIsOpen(false)

    const handleNavigate = (page) => {
        onNavigate(page)
        closeMenu()
        setIsHovered(false)
    }

    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);

    const getInstalledGameIds = () => {
        try {
            const saved = localStorage.getItem('modhub_detected_games');
            if (saved) return JSON.parse(saved).map(g => g.id);
        } catch (e) {}
        return [];
    };

    const getFavoriteGameIds = () => {
        try {
            const saved = localStorage.getItem('modhub_favorites');
            if (saved) return JSON.parse(saved);
        } catch (e) {}
        return [];
    };

    useEffect(() => {
        if (!searchTerm || searchTerm.trim() === '') {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        const query = searchTerm.toLowerCase();
        const installed = getInstalledGameIds();
        const favorites = getFavoriteGameIds();

        const matches = modDatabase.filter(g => 
            g.title.toLowerCase().includes(query) || 
            g.id.includes(query)
        ).slice(0, 5).map(g => {
            const isRunning = runningGames && runningGames.some(rg => rg.gameId === g.id);
            return {
                ...g,
                isInstalled: installed.includes(g.id),
                isFavorite: favorites.includes(g.id),
                active: isRunning
            };
        });

        setSuggestions(matches);
        setShowSuggestions(true);
    }, [searchTerm, runningGames]);

    return (
        <>
            {/* Mobile Menu Toggle */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="lg:hidden fixed top-[40px] left-4 z-50 w-11 h-11 glass-panel rounded-2xl flex items-center justify-center hover:bg-white/5 transition-all text-white"
                aria-label="Toggle menu"
            >
                {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>

            {/* Overlay for mobile */}
            {isOpen && (
                <div
                    className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity"
                    onClick={closeMenu}
                />
            )}

            {/* Sidebar - Executive Floating Design */}
            <aside
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                className={`fixed left-0 top-0 h-screen bg-[#0a0a0c]/80 backdrop-blur-3xl border-r border-white/[0.03] z-40 transition-all duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)] flex flex-col pt-8 shadow-[20px_0_40px_rgba(0,0,0,0.5)] lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'} ${isCollapsed ? 'w-20' : 'w-[280px]'}`}
            >
                {/* Header Logo */}
                <div className="h-20 flex items-center px-6 shrink-0 relative justify-between">
                    <div className="absolute bottom-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
                    <div className="flex items-center gap-3 overflow-hidden">
                        <div className="flex items-center justify-center drop-shadow-[0_0_20px_rgba(245,158,11,0.4)] min-w-[32px]">
                            <Logo className="w-8 h-8" />
                        </div>
                        <h1 className={`text-2xl font-display font-bold text-white tracking-tight transition-opacity duration-300 ${isCollapsed ? 'opacity-0 w-0' : 'opacity-100'}`}>
                            Gemule<span className="text-accent-primary">.</span>
                        </h1>
                    </div>
                </div>

                {/* Search Bar */}
                <div className="px-4 mt-4 mb-2 transition-all duration-300 relative z-50">
                    <div className={`relative group flex items-center transition-all duration-300 ${isCollapsed ? 'justify-center w-12 h-12 rounded-2xl bg-white/[0.03] mx-auto hover:bg-white/[0.08] cursor-pointer' : 'h-10'}`}>
                        {isCollapsed ? (
                            <Search className="w-5 h-5 text-white/40 group-hover:text-white/90" />
                        ) : (
                            <>
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 group-focus-within:text-accent-primary transition-colors" />
                                <input
                                    type="text"
                                    value={searchTerm || ''}
                                    onChange={(e) => setSearchTerm && setSearchTerm(e.target.value)}
                                    onFocus={() => { if (searchTerm) setShowSuggestions(true); }}
                                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                                    placeholder="Suchen..."
                                    className="w-full h-full pl-9 pr-3 bg-white/[0.03] border border-white/5 rounded-xl text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-accent-primary/50 focus:ring-2 focus:ring-accent-primary/20 hover:bg-white/[0.05] transition-all"
                                />
                            </>
                        )}
                    </div>
                    
                    {/* Suggestions Dropdown (Flyout to the right) */}
                    {!isCollapsed && showSuggestions && suggestions.length > 0 && (
                        <div className="absolute top-0 left-[calc(100%-8px)] w-[340px] bg-[#0a0a0c]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-[0_30px_60px_rgba(0,0,0,0.7)] overflow-hidden z-[100] animate-slideUp">
                            <div className="px-4 py-3 border-b border-white/5 bg-white/[0.02]">
                                <span className="text-[10px] font-bold uppercase tracking-widest text-white/40">Vorgeschlagene Spiele</span>
                            </div>
                            <div className="max-h-[400px] overflow-y-auto hide-scrollbar p-2">
                                {suggestions.map(game => (
                                    <div 
                                        key={game.id} 
                                        onClick={() => {
                                            if (setSearchTerm) setSearchTerm(game.title);
                                            setShowSuggestions(false);
                                            handleNavigate('library');
                                        }}
                                        className="px-4 py-3 hover:bg-white/5 cursor-pointer rounded-xl transition-colors flex items-center justify-between group"
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className="text-white text-sm font-semibold truncate group-hover:text-accent-primary transition-colors">{game.title}</div>
                                            <div className="flex items-center gap-2 mt-1.5 h-4">
                                                {game.isInstalled && (
                                                    <span className="text-[9px] font-bold uppercase tracking-wider text-accent-primary bg-accent-primary/10 px-1.5 py-0.5 rounded">Installiert</span>
                                                )}
                                                {game.isFavorite && (
                                                    <span className="text-[9px] font-bold uppercase tracking-wider text-yellow-500 bg-yellow-500/10 px-1.5 py-0.5 rounded flex items-center gap-1">
                                                        <Star className="w-2.5 h-2.5 fill-current" /> Favorit
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <ChevronRight className="w-4 h-4 text-white/20 group-hover:text-accent-primary group-hover:translate-x-1 transition-all shrink-0" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Navigation Items */}
                <nav className={`p-4 flex-1 flex flex-col gap-1.5 overflow-y-auto hide-scrollbar ${isCollapsed ? 'items-center mt-2' : ''}`}>
                    <NavItem
                        icon={<Compass className="w-[18px] h-[18px]" />}
                        label="Entdecken"
                        active={currentPage === 'discover'}
                        onClick={() => handleNavigate('discover')}
                        isCollapsed={isCollapsed}
                    />
                    <NavItem
                        icon={<LayoutGrid className="w-[18px] h-[18px]" />}
                        label="Bibliothek"
                        active={currentPage === 'library'}
                        onClick={() => handleNavigate('library')}
                        isCollapsed={isCollapsed}
                    />
                    <NavItem
                        icon={<Clock className="w-[18px] h-[18px]" />}
                        label="Aktive Mods"
                        active={currentPage === 'active-mods'}
                        onClick={() => handleNavigate('active-mods')}
                        isCollapsed={isCollapsed}
                    />
                    <NavItem
                        icon={<Star className="w-[18px] h-[18px]" />}
                        label="Favoriten"
                        active={currentPage === 'favorites'}
                        onClick={() => handleNavigate('favorites')}
                        isCollapsed={isCollapsed}
                    />
                    
                    {!isCollapsed ? (
                        <div className="mt-8 mb-2 px-4 text-[10px] font-bold text-white/30 uppercase tracking-[0.2em]">
                            System Control
                        </div>
                    ) : (
                        <div className="mt-6 mb-2 w-full h-px bg-white/5" />
                    )}
                    
                    {user?.role === 'admin' && (
                        <>
                            <NavItem
                                icon={<Crown className="w-[18px] h-[18px] text-purple-400" />}
                                label="Admin Dashboard"
                                active={currentPage === 'admin'}
                                onClick={() => handleNavigate('admin')}
                                isCollapsed={isCollapsed}
                            />
                            <NavItem
                                icon={<Terminal className="w-[18px] h-[18px] text-green-400" />}
                                label="System Logs"
                                active={currentPage === 'admin/logs'}
                                onClick={() => handleNavigate('admin/logs')}
                                isCollapsed={isCollapsed}
                            />
                        </>
                    )}
                    
                    <NavItem
                        icon={<Settings className="w-[18px] h-[18px]" />}
                        label="Einstellungen"
                        active={currentPage === 'settings'}
                        onClick={() => handleNavigate('settings')}
                        isCollapsed={isCollapsed}
                    />
                </nav>

                {/* User Profile Footer */}
                <div className={`p-4 pb-8 shrink-0 flex justify-center ${isCollapsed ? '' : 'px-6'}`}>
                    <div className={`flex items-center gap-3 p-2 rounded-2xl bg-white/[0.02] hover:bg-white/[0.04] transition-colors cursor-pointer border border-white/[0.03] hover:border-white/[0.06] ${isCollapsed ? 'justify-center w-12 h-12' : 'w-full px-3 py-2'}`}>
                        <div className="w-8 h-8 rounded-full bg-black/40 border border-white/10 flex items-center justify-center shadow-inner relative overflow-hidden shrink-0">
                            <div className="absolute inset-0 bg-gradient-to-br from-accent-primary/20 to-accent-secondary/20 opacity-0 hover:opacity-100 transition-opacity"></div>
                            <User className="w-4 h-4 text-white/80" />
                        </div>
                        {!isCollapsed && (
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-white/90 truncate">Elite Access</div>
                                <div className="text-xs text-accent-primary truncate font-medium">Pro Member</div>
                            </div>
                        )}
                    </div>
                </div>
            </aside>
        </>
    )
}

// NavItem Component with Premium Liquid Selection
const NavItem = ({ icon, label, active = false, onClick, isCollapsed }) => {
    return (
        <button
            onClick={onClick}
            title={isCollapsed ? label : ''}
            className={`flex items-center rounded-2xl font-medium transition-all duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)] group relative overflow-hidden ${isCollapsed ? 'justify-center w-12 h-12 p-0' : 'gap-3 px-4 py-3 w-full text-left'} ${active
                    ? 'text-white'
                    : 'text-white/40 hover:text-white/80'
                }`}
        >
            {/* Active Background Pill with ambient glow */}
            <div className={`absolute inset-0 rounded-2xl transition-all duration-500 ease-out ${
                active 
                    ? 'bg-gradient-to-r from-accent-primary/[0.08] via-accent-primary/[0.04] to-transparent border border-accent-primary/[0.08] opacity-100' 
                    : 'bg-transparent border border-transparent opacity-0 group-hover:bg-white/[0.03] group-hover:border-white/[0.04] group-hover:opacity-100'
            }`} />
            
            {/* Hover shimmer effect */}
            <div className={`absolute inset-0 rounded-2xl transition-opacity duration-700 pointer-events-none ${
                active ? 'opacity-0' : 'opacity-0 group-hover:opacity-100'
            }`}>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.02] to-transparent" />
            </div>
            
            {/* Active Indicator Glow Bar */}
            <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-[3px] rounded-r-full transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] ${
                active 
                    ? 'h-7 bg-gradient-to-b from-accent-primary to-accent-secondary shadow-[0_0_14px_rgba(245,158,11,0.5)] opacity-100' 
                    : 'h-0 opacity-0 group-hover:h-4 group-hover:opacity-30 group-hover:bg-white/40'
            }`} />
            
            <div className={`relative z-10 transition-all duration-300 ${
                active 
                    ? 'text-accent-primary scale-110 drop-shadow-[0_0_8px_rgba(245,158,11,0.3)]' 
                    : 'group-hover:scale-105 group-hover:text-white/70'
            }`}>
                {icon}
            </div>
            
            {!isCollapsed && (
                <span className={`relative z-10 text-sm tracking-wide transition-all duration-300 ${
                    active 
                        ? 'font-semibold text-white' 
                        : 'font-medium group-hover:translate-x-0.5'
                } truncate`}>{label}</span>
            )}
            
            {/* Active subtle ambient light */}
            {active && (
                <div className="absolute -right-4 top-1/2 -translate-y-1/2 w-16 h-16 bg-accent-primary/[0.06] rounded-full blur-2xl pointer-events-none" />
            )}
        </button>
    )
}

export default Sidebar
